import { createContext, useContext, useMemo, type ReactNode } from "react"
import { useUptimeInlineListQuery } from "@/v2-backoffice-client/uptime/inline/list/uptime.inline.list.query"
import { MAP_LAYER_MONITOR_SOURCE } from "@/v2-backoffice-client/layer/layer.constants"
import type { UptimeInlineFragment } from "@/v2-backoffice-client/uptime/inline/__types__/uptime.inline.fragment"

type Ctx = {
  byFeatureCode: Map<string, UptimeInlineFragment>
  loading: boolean
}

const EMPTY_CTX: Ctx = {
  byFeatureCode: new Map(),
  loading: false,
}

const LayerUptimeMapContext = createContext<Ctx>(EMPTY_CTX)

export type LayerUptimeMapProviderProps = {
  children: ReactNode
}

// Wraps a subtree (typically the layer table) with one shared uptime
// lookup. The list query is run once and bucketed client-side -- the
// schema doesn't currently expose a Source-scoped list and the slim
// fragment keeps the payload small enough that this is fine for our
// fleet size.
export function LayerUptimeMapProvider(
  props: Readonly<LayerUptimeMapProviderProps>,
) {
  const { data, loading } = useUptimeInlineListQuery({
    // Cells just want a snapshot; refetching on every mount would
    // hammer the API as the operator navigates the layer table.
    fetchPolicy: "cache-first",
  })

  const value = useMemo<Ctx>(() => {
    const map = new Map<string, UptimeInlineFragment>()
    for (const t of data?.getUptimeTargets ?? []) {
      if (t.Source !== MAP_LAYER_MONITOR_SOURCE) continue
      map.set(t.Identifier, t)
    }
    return { byFeatureCode: map, loading }
  }, [data, loading])

  return (
    <LayerUptimeMapContext.Provider value={value}>
      {props.children}
    </LayerUptimeMapContext.Provider>
  )
}

export function useLayerUptime(featureCode: string | null | undefined) {
  const ctx = useContext(LayerUptimeMapContext)
  const target = featureCode ? ctx.byFeatureCode.get(featureCode) : undefined
  return {
    target: target ?? null,
    // Loading only matters until the map is populated; once we have
    // entries any miss is a real "no monitor" not a pending fetch.
    loading: ctx.loading && ctx.byFeatureCode.size === 0,
  }
}
